// 安全认证工具模块
// 提供安全的登录态管理，防止 XSS、CSRF 等攻击

// 生成设备指纹（用于会话绑定）
export const generateDeviceFingerprint = () => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText('Device fingerprint', 2, 2);
  
  const fingerprint = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    // eslint（no-restricted-globals）不允许直接使用全局 screen 标识符
    screen: `${(typeof window !== 'undefined' && window.screen ? window.screen.width : 0)}x${(typeof window !== 'undefined' && window.screen ? window.screen.height : 0)}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    canvas: canvas.toDataURL().slice(0, 50) // 简化版
    // 注意：不包含 timestamp，确保设备指纹在同一设备上保持一致
  };
  
  // 生成哈希
  const hash = btoa(JSON.stringify(fingerprint))
    .replace(/[^a-zA-Z0-9]/g, '')
    .substring(0, 32);
  
  return hash;
};

// 获取或创建设备指纹（存储在 sessionStorage，页面刷新后清除）
export const getDeviceFingerprint = () => {
  let fp = sessionStorage.getItem('device_fingerprint');
  if (!fp) {
    fp = generateDeviceFingerprint();
    sessionStorage.setItem('device_fingerprint', fp);
  }
  return fp;
};

// CSRF Token 管理
let csrfTokenPromise = null;
const CSRF_TOKEN_KEY = 'csrf_token';
const CSRF_TOKEN_EXPIRES_AT_KEY = 'csrf_token_expires_at';
// 服务端目前是 1 小时，这里提前 2 分钟刷新，避免边界时间失败
const CSRF_REFRESH_SAFETY_MS = 2 * 60 * 1000;

export const getCsrfToken = async () => {
  const existing = sessionStorage.getItem(CSRF_TOKEN_KEY);
  const exp = Number(sessionStorage.getItem(CSRF_TOKEN_EXPIRES_AT_KEY) || 0);
  if (existing) {
    // 若无 expiresAt（旧数据）不能继续无期限复用：服务端 token 有效期有限，会导致登录 403。
    // 这里视为“需要刷新”，走下面的 fetch 分支重新拉取。
    if (!Number.isFinite(exp) || exp <= 0) {
      // fallthrough
    } else if (Date.now() < (exp - CSRF_REFRESH_SAFETY_MS)) {
      return existing;
    }
  }
  
  // 如果正在获取，返回同一个 Promise
  if (csrfTokenPromise) {
    return csrfTokenPromise;
  }
  
  // 从服务端获取 CSRF Token
  console.log('[CSRF] Fetching CSRF token from server...');
  csrfTokenPromise = fetch('/api/auth/csrf-token', {
    credentials: 'include'
  })
    .then(async (res) => {
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error('[CSRF] Failed to fetch CSRF token:', res.status, text);
        throw new Error(`Failed to fetch CSRF token: ${res.status} ${text}`.trim());
      }
      return res.json();
    })
    .then(data => {
      const token = data.csrfToken;
      console.log('[CSRF] Received token:', token ? token.substring(0, 8) + '...' : 'null', 'ExpiresAt:', data.expiresAt ? new Date(data.expiresAt).toISOString() : 'null');
      if (token) {
        sessionStorage.setItem(CSRF_TOKEN_KEY, token);
        // 兼容服务端未返回 expiresAt 的情况
        if (data.expiresAt) {
          sessionStorage.setItem(CSRF_TOKEN_EXPIRES_AT_KEY, String(data.expiresAt));
        } else {
          sessionStorage.removeItem(CSRF_TOKEN_EXPIRES_AT_KEY);
        }
        console.log('[CSRF] Token stored in sessionStorage');
      } else {
        console.warn('[CSRF] No token in response!');
      }
      csrfTokenPromise = null;
      return token;
    })
    .catch(err => {
      console.error('[CSRF] Failed to fetch CSRF token:', err);
      csrfTokenPromise = null;
      // 不要生成本地 fallback token：服务端无法识别，只会导致后续所有请求 403（Invalid CSRF）
      // 这里直接抛错，让调用方决定是否重试/提示“网络异常”
      throw err;
    });
  
  return csrfTokenPromise;
};

// 同步获取 CSRF Token（如果已存在）
export const getCsrfTokenSync = () => {
  return sessionStorage.getItem(CSRF_TOKEN_KEY) || '';
};

export const clearCsrfToken = () => {
  try {
    sessionStorage.removeItem(CSRF_TOKEN_KEY);
    sessionStorage.removeItem(CSRF_TOKEN_EXPIRES_AT_KEY);
  } catch (_) {}
};

// 清除所有认证相关的存储
export const clearAuthStorage = () => {
  try {
    // 尽力调用登出 API（用于清理服务端 Cookie）。
    // 注意：不要在这里无条件触发登出失败导致更多噪音；只在本地仍持有 CSRF/指纹时尝试一次。
    try {
      const csrfToken = getCsrfTokenSync();
      const deviceFingerprint = sessionStorage.getItem('device_fingerprint') || '';
      if (csrfToken && deviceFingerprint) {
        fetch('/api/auth/logout', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'X-CSRF-Token': csrfToken,
            'X-Device-Fingerprint': deviceFingerprint
          }
        }).catch(() => {});
      }
    } catch (_) {}

    // 清除本地存储（仅保留非敏感设置）
    const playerSettings = localStorage.getItem('musicPlayerSettings');
    const usernameHistory = localStorage.getItem('tenryu.usernameHistory');
    localStorage.clear();
    if (playerSettings) {
      localStorage.setItem('musicPlayerSettings', playerSettings);
    }
    if (usernameHistory) {
      localStorage.setItem('tenryu.usernameHistory', usernameHistory);
    }
    
    // 清除会话存储
    sessionStorage.clear();
    
  } catch (e) {
    console.error('Failed to clear auth storage:', e);
  }
};

// 安全的请求配置（自动添加 CSRF Token）
export const getSecureRequestConfig = async (config = {}) => {
  const csrfToken = await getCsrfToken();
  const headers = {
    ...config.headers,
    'X-CSRF-Token': csrfToken,
    'X-Device-Fingerprint': getDeviceFingerprint()
  };
  
  return {
    ...config,
    headers,
    credentials: 'include' // 重要：包含 Cookie
  };
};

// 同步版本（如果 CSRF Token 已存在）
export const getSecureRequestConfigSync = (config = {}) => {
  const csrfToken = getCsrfTokenSync();
  const headers = {
    ...config.headers,
    'X-CSRF-Token': csrfToken,
    'X-Device-Fingerprint': getDeviceFingerprint()
  };
  
  return {
    ...config,
    headers,
    credentials: 'include'
  };
};

// 检查是否在安全上下文（HTTPS）中
export const isSecureContext = () => {
  return window.isSecureContext || window.location.protocol === 'https:';
};

// Token 刷新状态管理
class TokenRefreshManager {
  constructor() {
    this.refreshPromise = null;
    this.refreshing = false;
  }

  async refreshToken() {
    if (this.refreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshing = true;

    // 关键：getSecureRequestConfig 是 async，必须 await，否则请求不会带 CSRF/指纹，导致 403。
    const config = await getSecureRequestConfig({
      method: 'POST',
      cache: 'no-store'
    });

    this.refreshPromise = fetch('/api/auth/refresh', config)
      .then(res => {
        if (!res.ok) {
          throw new Error('Token refresh failed');
        }
        return res.json();
      })
      .finally(() => {
        this.refreshing = false;
        this.refreshPromise = null;
      });

    return this.refreshPromise;
  }

  reset() {
    this.refreshing = false;
    this.refreshPromise = null;
  }
}

export const tokenRefreshManager = new TokenRefreshManager();

// 安全的 Fetch 封装（自动处理 Token 刷新）
export const secureFetch = async (url, options = {}) => {
  const isCsrfErrorResponse = async (resp) => {
    try {
      const cloned = resp.clone();
      const data = await cloned.json().catch(() => null);
      const msg = String(data?.error || data?.message || '').toLowerCase();
      return msg.includes('csrf') || msg.includes('xsrf');
    } catch (_) {
      return false;
    }
  };

  let config = await getSecureRequestConfig(options);
  let response = await fetch(url, config);

  // CSRF 失效：清 token -> 重新拉取 -> 重试一次
  if (response.status === 403 && await isCsrfErrorResponse(response)) {
    try {
      clearCsrfToken();
      await getCsrfToken();
      config = await getSecureRequestConfig(options);
      response = await fetch(url, config);
    } catch (_) {
      // fallthrough：交给调用方处理
    }
  }
  
  // 如果 Token 过期，尝试刷新
  if (response.status === 401) {
    try {
      await tokenRefreshManager.refreshToken();
      // 重试原请求（重新获取配置以包含新的 Token）
      const retryConfig = await getSecureRequestConfig(options);
      response = await fetch(url, retryConfig);
    } catch (e) {
      // 刷新失败，清除认证状态
      clearAuthStorage();
      throw new Error('Authentication expired');
    }
  }
  
  return response;
};

// 安全的 Axios 拦截器配置
export const setupAxiosInterceptors = (axios) => {
  // 请求拦截器
  axios.interceptors.request.use(
    async (config) => {
      const csrfToken = await getCsrfToken();
      const deviceFingerprint = getDeviceFingerprint();
      
      config.headers = {
        ...config.headers,
        'X-CSRF-Token': csrfToken,
        'X-Device-Fingerprint': deviceFingerprint
      };
      
      // 确保发送 Cookie
      config.withCredentials = true;
      
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // 响应拦截器（处理 Token 刷新）
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // CSRF 失效：清 token -> 重新拉取 -> 重试一次
      if (error.response?.status === 403 && !originalRequest?._csrfRetry) {
        const msg = String(error.response?.data?.error || error.response?.data?.message || '').toLowerCase();
        if (msg.includes('csrf') || msg.includes('xsrf')) {
          originalRequest._csrfRetry = true;
          clearCsrfToken();
          await getCsrfToken();
          const csrfToken = getCsrfTokenSync();
          const deviceFingerprint = getDeviceFingerprint();
          originalRequest.headers = {
            ...(originalRequest.headers || {}),
            'X-CSRF-Token': csrfToken,
            'X-Device-Fingerprint': deviceFingerprint
          };
          originalRequest.withCredentials = true;
          return axios(originalRequest);
        }
      }

      // 如果 Token 过期且未重试过
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          await tokenRefreshManager.refreshToken();
          // 重试原请求
          return axios(originalRequest);
        } catch (refreshError) {
          // 刷新失败，清除认证状态
          clearAuthStorage();
          window.location.href = '/login-check';
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};
